import React, { useState } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { ResumeType, RecruitingDirectionDto } from '../../api-client';

interface ReferralCreateFormProps {
  directions: RecruitingDirectionDto[];
  onSubmit: (
    directionId: string,
    candidateName: string,
    comment?: string | null,
    resumeType?: ResumeType,
    resumeFileUrl?: string | null,
    resumeFileName?: string | null,
    resumeUrl?: string | null
  ) => Promise<void>;
  onCancel?: () => void;
  onUploadResume?: (file: File) => Promise<{ url: string; fileName: string }>;
  submitLabel?: string;
}

const ReferralCreateForm: React.FC<ReferralCreateFormProps> = ({
  directions,
  onSubmit,
  onCancel,
  onUploadResume,
  submitLabel = 'Создать заявку',
}) => {
  const [directionId, setDirectionId] = useState<string>('');
  const [candidateName, setCandidateName] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [resumeType, setResumeType] = useState<ResumeType>(ResumeType.FILE);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeUrl, setResumeUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!directionId || !candidateName) {
      setError('Пожалуйста, заполните обязательные поля');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let fileUrl: string | null = null;
      let fileName: string | null = null;

      if (resumeType === ResumeType.FILE && resumeFile && onUploadResume) {
        const uploadResult = await onUploadResume(resumeFile);
        fileUrl = uploadResult.url;
        fileName = uploadResult.fileName;
      }

      await onSubmit(
        directionId,
        candidateName,
        comment || null,
        resumeType,
        resumeType === ResumeType.FILE ? fileUrl : null,
        resumeType === ResumeType.FILE ? fileName : null,
        resumeType === ResumeType.URL ? resumeUrl || null : null
      );

      setDirectionId('');
      setCandidateName('');
      setComment('');
      setResumeFile(null);
      setResumeUrl('');
    } catch (err: any) {
      setError(err.message || 'Ошибка при создании заявки');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form.Group className="mb-3">
        <Form.Label>Направление *</Form.Label>
        <Form.Select
          value={directionId}
          onChange={(e) => setDirectionId(e.target.value)}
          disabled={loading}
          required
        >
          <option value="">Выберите направление</option>
          {directions.map((dir) => (
            <option key={dir.id} value={dir.id}>
              {dir.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Имя кандидата *</Form.Label>
        <Form.Control
          type="text"
          placeholder="Введите имя кандидата"
          value={candidateName}
          onChange={(e) => setCandidateName(e.target.value)}
          disabled={loading}
          required
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Комментарий (необязательно)</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder="Добавьте комментарий к заявке..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={loading}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Тип резюме</Form.Label>
        <Form.Check
          type="radio"
          id="resume-type-file"
          label="Загрузить файл"
          checked={resumeType === ResumeType.FILE}
          onChange={() => setResumeType(ResumeType.FILE)}
          disabled={loading}
        />
        <Form.Check
          type="radio"
          id="resume-type-url"
          label="Ссылка на резюме"
          checked={resumeType === ResumeType.URL}
          onChange={() => setResumeType(ResumeType.URL)}
          disabled={loading}
        />
      </Form.Group>
      {resumeType === ResumeType.FILE ? (
        <Form.Group className="mb-3">
          <Form.Label>Файл резюме</Form.Label>
          <Form.Control
            type="file"
            onChange={(e) => {
              const files = (e.target as HTMLInputElement).files;
              if (files && files.length > 0) {
                setResumeFile(files[0]);
              }
            }}
            disabled={loading}
            accept=".pdf,.doc,.docx,.txt"
          />
          <Form.Text className="text-muted">Поддерживаемые форматы: PDF, DOC, DOCX, TXT</Form.Text>
        </Form.Group>
      ) : (
        <Form.Group className="mb-3">
          <Form.Label>Ссылка на резюме</Form.Label>
          <Form.Control
            type="url"
            placeholder="https://example.com/resume.pdf"
            value={resumeUrl}
            onChange={(e) => setResumeUrl(e.target.value)}
            disabled={loading}
          />
        </Form.Group>
      )}
      <div className="d-flex gap-2">
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Создание...
            </>
          ) : (
            submitLabel
          )}
        </Button>
        {onCancel && (
          <Button variant="outline-secondary" onClick={onCancel} disabled={loading}>
            Отмена
          </Button>
        )}
      </div>
    </Form>
  );
};

export default ReferralCreateForm;
